/**
 * Category-specific colors & sizes for wedding pre-loved listings.
 */

export const WEDDING_COLOR_SETS = {
  bridal: [
    'White',
    'Ivory',
    'Cream',
    'Champagne',
    'Blush',
    'Light Pink',
    'Rose',
    'Dusty Rose',
    'Mauve',
    'Lavender',
    'Silver',
    'Gold',
    'Champagne Gold',
    'Nude',
    'Other',
  ],
  mens: [
    'Black',
    'Charcoal',
    'Navy',
    'Midnight Blue',
    'Grey',
    'Light Grey',
    'Brown',
    'Tan',
    'White',
    'Ivory',
    'Burgundy',
    'Other',
  ],
  shoes: [
    'Black',
    'White',
    'Ivory',
    'Nude',
    'Silver',
    'Gold',
    'Rose Gold',
    'Champagne',
    'Red',
    'Navy',
    'Other',
  ],
  accessories: [
    'Gold',
    'Rose Gold',
    'Silver',
    'Pearl',
    'White',
    'Ivory',
    'Black',
    'Crystal',
    'Multi',
    'Other',
  ],
  decor: [
    'White',
    'Ivory',
    'Gold',
    'Silver',
    'Blush',
    'Green',
    'Blush Pink',
    'Burgundy',
    'Navy',
    'Black',
    'Multi',
    'Other',
  ],
  neutral: ['Not applicable', 'Mixed', 'Other'],
};

/** @typedef {{ label: string, sizes: string[] }} SizeGroup */

/** @type {Record<string, { label: string, hint: string, groups: SizeGroup[] }>} */
export const WEDDING_SIZE_PROFILES = {
  apparel_women: {
    label: 'Clothing size',
    hint: 'Select every size you have available (buyers often filter by size).',
    groups: [
      {
        label: 'Letter sizes',
        sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'],
      },
      {
        label: 'US dress sizes',
        sizes: ['0', '2', '4', '6', '8', '10', '12', '14', '16', '18', '20', '22'],
      },
    ],
  },
  apparel_men: {
    label: 'Clothing size',
    hint: 'Select jacket/chest or letter sizes you have in stock.',
    groups: [
      {
        label: 'Letter sizes',
        sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'],
      },
      {
        label: 'Suit / chest (inches)',
        sizes: ['34', '36', '38', '40', '42', '44', '46', '48', '50', '52'],
      },
      {
        label: 'Dress shirt (neck)',
        sizes: ['14', '14.5', '15', '15.5', '16', '16.5', '17', '17.5', '18'],
      },
    ],
  },
  shoes_women: {
    label: 'Shoe size',
    hint: 'Use EU sizes for heels and bridal shoes (most common in Egypt).',
    groups: [
      {
        label: 'EU',
        sizes: ['35', '36', '37', '38', '39', '40', '41', '42', '43'],
      },
      {
        label: 'US women',
        sizes: ['5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11'],
      },
      {
        label: 'UK',
        sizes: ['2', '3', '4', '5', '6', '7', '8', '9'],
      },
    ],
  },
  shoes_men: {
    label: 'Shoe size',
    hint: 'Select EU or UK formal shoe sizes you have available.',
    groups: [
      {
        label: 'EU',
        sizes: ['39', '40', '41', '42', '43', '44', '45', '46', '47', '48'],
      },
      {
        label: 'UK',
        sizes: ['6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '12'],
      },
      {
        label: 'US men',
        sizes: ['7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13'],
      },
    ],
  },
  accessories: {
    label: 'Size',
    hint: 'Most accessories are one size or adjustable.',
    groups: [
      {
        label: '',
        sizes: ['One Size', 'Adjustable', 'Petite', 'Standard', 'Plus'],
      },
    ],
  },
  veil: {
    label: 'Veil length',
    hint: 'Choose the length(s) you are listing.',
    groups: [
      {
        label: '',
        sizes: ['Shoulder', 'Elbow', 'Fingertip', 'Waltz', 'Chapel', 'Cathedral', 'One Size'],
      },
    ],
  },
};

/** Maps wedding hub category → color palette + size profile key */
export const WEDDING_CATEGORY_ATTRIBUTE_MAP = {
  'Bridal Gowns and Essentials': { colorSet: 'bridal', sizeProfile: 'apparel_women' },
  'Bridesmaids / soiree dresses': { colorSet: 'bridal', sizeProfile: 'apparel_women' },
  'Bridesmaids Dresses': { colorSet: 'bridal', sizeProfile: 'apparel_women' },
  'Bridal Veil': { colorSet: 'bridal', sizeProfile: 'veil' },
  'Bridal Accessories': { colorSet: 'accessories', sizeProfile: 'accessories' },
  'Bridal Shoes': { colorSet: 'shoes', sizeProfile: 'shoes_women' },
  'Groom Suits & Essentials': { colorSet: 'mens', sizeProfile: 'apparel_men' },
  'Suits & Prom Wear': { colorSet: 'mens', sizeProfile: 'apparel_men' },
  'Grooms Accessories': { colorSet: 'accessories', sizeProfile: 'accessories' },
  'Men Formal shoes': { colorSet: 'shoes', sizeProfile: 'shoes_men' },
  'Men Shoes': { colorSet: 'shoes', sizeProfile: 'shoes_men' },
  'Event Decorations': { colorSet: 'decor', sizeProfile: null },
};

/**
 * @param {string} category
 * @returns {{ colors: string[], sizeProfile: object|null, requiresSize: boolean }|null}
 */
export function getWeddingPreLovedAttributes(category) {
  if (!category) return null;
  const map = WEDDING_CATEGORY_ATTRIBUTE_MAP[category];
  if (!map) return null;
  const colors = WEDDING_COLOR_SETS[map.colorSet] || WEDDING_COLOR_SETS.bridal;
  const sizeProfile = map.sizeProfile ? WEDDING_SIZE_PROFILES[map.sizeProfile] : null;
  return {
    colors,
    sizeProfile,
    requiresSize: Boolean(sizeProfile),
  };
}
